import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { FiFilter, FiSearch, FiCheckCircle, FiLock, FiStar, FiUserPlus, FiLogIn, FiDollarSign, FiRepeat } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import MediaLogos from './MediaLogos';
import { shopProducts, shopCategories } from '../data/shopData';

const Shop = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currency, setCurrency] = useState('MXN');
  
  const location = useLocation();
  const campusLink = "https://app.onlineclassesenglish.com";
  const whatsappNumber = "+529844594738";

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const search = params.get('search');
    if (search) {
      setSearchTerm(search);
    }
  }, [location]);

  const filteredProducts = shopProducts.filter(product => {
    const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-4">
      <Helmet>
        <title>Catálogo de Programas | Online Classes English</title>
        <meta name="description" content="Explora nuestros programas de inglés, especialidades y planes mensuales para estudiantes en México." />
      </Helmet>

      <ProductModal product={selectedProduct} isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} />
      
      <div className="max-w-7xl mx-auto">
        {/* Trust Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-zinc-900/50 p-4 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-6 text-xs md:text-sm font-bold text-gray-400">
            <span className="flex items-center gap-1"><SafeIcon icon={FiLock} className="text-green-500"/> SSL Secure</span>
            <span className="flex items-center gap-1"><SafeIcon icon={FiCheckCircle} className="text-blue-500"/> Verified</span>
            <span className="flex items-center gap-1"><SafeIcon icon={FiStar} className="text-yellow-500"/> University Level</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-black border border-zinc-700 rounded-full p-1">
              <button 
                onClick={() => setCurrency('MXN')}
                className={`px-4 py-1 rounded-full text-[10px] font-black transition-all ${currency === 'MXN' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}
              >
                MXN
              </button>
              <button 
                onClick={() => setCurrency('USD')}
                className={`px-4 py-1 rounded-full text-[10px] font-black transition-all ${currency === 'USD' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}
              >
                USD
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase tracking-tighter italic">
            Catálogo de <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-yellow-500 to-green-600">Herramientas</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Selecciona el programa que impulsará tu carrera global. Precios adaptados para Latinoamérica.
          </p>
        </div>

        {/* Categories & Search */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 bg-zinc-900/50 p-6 rounded-2xl border border-zinc-800 backdrop-blur-sm sticky top-28 z-40 shadow-2xl">
          <div className="flex flex-wrap gap-2 justify-center">
            {shopCategories.map(cat => (
              <button 
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs font-black transition-all flex items-center gap-2 uppercase tracking-widest ${activeCategory === cat.id ? 'bg-white text-black' : 'bg-black text-gray-500 hover:text-white border border-zinc-700'}`}
              >
                <SafeIcon icon={cat.icon} /> {cat.name}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input 
              type="text" 
              placeholder="Buscar programa..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black border border-zinc-700 rounded-full py-2 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-yellow-500 transition-colors"
            />
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} onOpenDetails={setSelectedProduct} currency={currency} />
          ))}
        </div>

        {/* Comparison Table Section */}
        <div className="mb-20">
           <MediaLogos />
        </div>
      </div>
    </div>
  );
};

export default Shop;